<template>
  <main class="flex">
    <!-- EDIT DIALOG -->
    <Dialog v-model:visible="enableEdit" header="Edit Service Invoice" modal :closable="false"
      class="w-full max-w-5xl p-6">
      <loading v-if="isLoading" />
      <form class="grid grid-cols-2 md:grid-cols-4 gap-6">
        <!-- General Info Section -->
        <div class="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 border-r pr-6">
          <div class="col-span-2 text-lg font-semibold mb-4 border-b-2 pb-2 tracking-wide">
            General Information
          </div>

          <div>
            <label class="text-sm font-medium mb-1 block">Invoice Number</label>
            <input v-model="editDataPayload.invoice_number" placeholder="Invoice Number"
              class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div>
            <label class="text-sm font-medium mb-1 block">Date</label>
            <input type="date" v-model="editDataPayload.invoice_date"
              class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div>
            <label class="text-sm font-medium mb-1 block">Sold To</label>
            <input v-model="editDataPayload.soldTo" placeholder="Sold To"
              class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div>
            <label class="text-sm font-medium mb-1 block">Terms</label>
            <input v-model="editDataPayload.terms" placeholder="Terms"
              class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div>
            <label class="text-sm font-medium mb-1 block">TIN</label>
            <input v-model="editDataPayload.tin" placeholder="TIN No. Or 0 if not applicable"
              class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div>
            <label class="text-sm font-medium mb-1 block">OSCA/PWD ID No</label>
            <input v-model="editDataPayload.oscaId" placeholder="OSCA/PWD ID No"
              class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div class="col-span-2">
            <label class="text-sm font-medium mb-1 block">Address</label>
            <textarea v-model="editDataPayload.address" rows="4" placeholder="Address"
              class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]"></textarea>
          </div>

          <div class="col-span-2">
            <label class="text-sm font-medium mb-1 block">SC/PWD Signature</label>
            <input v-model="editDataPayload.signature" placeholder="SC/PWD Signature"
              class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>
        </div>

        <div class="col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Section Header -->
          <div class="col-span-2 text-lg font-semibold mb-4 border-b-2 pb-2 tracking-wide">
            Payment Information
          </div>

          <!-- Mode of Payment -->
          <div>
            <label class="text-sm font-medium mb-1 block">Mode of Payment</label>
            <select v-model="editDataPayload.mode_of_payment"
              class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]">
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>

          <!-- Check Number -->
          <div v-if="editDataPayload.mode_of_payment !== 'cash'">
            <label class="text-sm font-medium mb-1 block">Check Number</label>
            <input v-model="editDataPayload.Check_no" type="text" placeholder="Check Number"
              class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <!-- Bank -->
          <div v-if="editDataPayload.mode_of_payment !== 'cash'">
            <label class="text-sm font-medium mb-1 block">Bank</label>
            <input v-model="editDataPayload.Bank" placeholder="Bank"
              class="px-3 py-2 border rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <!-- Numeric Fields -->
          <div>
            <label class="text-sm font-medium mb-1 block">VATable Sales</label>
            <input v-model.number="editDataPayload.VATable_sales" @change="computeTransaction" type="number" step="0.01"
              placeholder="0.00"
              class="px-3 py-2 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div>
            <label class="text-sm font-medium mb-1 block">VAT Exempt Sales</label>
            <input v-model.number="editDataPayload.vat_Exempt_sales" @change="computeTransaction" type="number"
              step="0.01" placeholder="0.00"
              class="px-3 py-2 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div>
            <label class="text-sm font-medium mb-1 block">Zero-Rated Sales</label>
            <input v-model.number="editDataPayload.zero_rated_sales" @change="computeTransaction" type="number"
              step="0.01" placeholder="0.00"
              class="px-3 py-2 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <!-- With Withholding Checkbox -->
          <div class="col-span-2 flex items-center gap-2 mt-2">
            <input type="checkbox" id="withholding" v-model="editDataPayload.withWithholding"
              @change="computeTransaction" class="w-4 h-4 accent-[#F48921] rounded" />
            <label for="withholding" class="text-sm font-medium">With Withholding</label>
          </div>

          <!-- Computed Fields -->
          <div>
            <label class="text-sm font-medium mb-1 block">VAT Amount</label>
            <input disabled v-model.number="editDataPayload.vat_amount" type="number" step="0.01" placeholder="0.00"
              class="px-3 py-2 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div>
            <label class="text-sm font-medium mb-1 block">Amount</label>
            <input disabled v-model.number="editDataPayload.amount" type="number" step="0.01" placeholder="0.00"
              class="px-3 py-2 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div>
            <label class="text-sm font-medium mb-1 block">Less VAT</label>
            <input disabled v-model.number="editDataPayload.less_vat" type="number" step="0.01" placeholder="0.00"
              class="px-3 py-2 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div>
            <label class="text-sm font-medium mb-1 block">Amount Net of VAT</label>
            <input disabled v-model.number="editDataPayload.amount_net_of_vat" type="number" step="0.01"
              placeholder="0.00"
              class="px-3 py-2 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div>
            <label class="text-sm font-medium mb-1 block">Less SC/PWD Discount</label>
            <input disabled v-model.number="editDataPayload.less_sc_pwd_discount" type="number" step="0.01"
              placeholder="0.00"
              class="px-3 py-2 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div>
            <label class="text-sm font-medium mb-1 block">Less Withholding Tax</label>
            <input disabled v-model.number="editDataPayload.less_withholding_tax" type="number" step="0.01"
              placeholder="0.00"
              class="px-3 py-2 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>

          <div class="col-span-2">
            <label class="text-sm font-medium mb-1 block">Total Amount Due</label>
            <input disabled v-model.number="editDataPayload.total_amount_due" type="number" step="0.01"
              placeholder="0.00"
              class="px-3 py-2 border rounded-md w-full text-right focus:outline-none focus:ring-2 focus:ring-[#F48921]" />
          </div>
        </div>
      </form>

      <!-- Invoice Items Section -->
      <div class="col-span-2 border-t pt-4 mt-5">
        <div class="flex items-center justify-between text-lg font-semibold mb-4 border-b-2 pb-2 tracking-wide">
          <span>Invoice Items</span>

          <!-- <button
            type="button"
            @click="addInvoiceItem"
            class="px-4 py-2 bg-brand_orange3 text-white text-s rounded-md hover:bg-brand_orange1"
          >
            + Add Item
          </button> -->
        </div>

        <div v-for="(item, index) in editDataPayload.invoice_items" :key="item.id" class="flex gap-2 items-center mb-2">
          <!-- Description -->
          <input v-model="item.description" placeholder="Description"
            class="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#F48921]" />

          <!-- Quantity -->
          <input v-model.number="item.quantity" type="number" min="0" step="1" placeholder="Qty"
            class="w-20 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#F48921]" />

          <!-- Cost -->
          <input v-model.number="item.cost" type="number" min="0" step="0.01" placeholder="Cost"
            class="w-28 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#F48921]" />

          <!-- Total -->
          <input v-model.number="item.total" type="number" step="0.01" placeholder="Total"
            class="w-28 px-3 py-2 border rounded-md bg-gray-100 cursor-not-allowed" readonly />

          <!-- Remove button -->
          <button type="button" @click.prevent class="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">
            &times;
          </button>
        </div>
      </div>

      <!-- Buttons -->
      <div class="mt-6 flex justify-end gap-3">
        <button @click="enableEdit = false" class="px-5 py-2 rounded-md border hover:bg-gray-100 transition">
          Cancel
        </button>
        <button @click.prevent="submitForm"
          class="px-5 py-2 rounded-md bg-brand_orange3 text-white hover:bg-[#e07b1c] transition">
          Save
        </button>
      </div>
    </Dialog>

    <!-- Loading & Print -->
    <loading v-if="isLoading" />
    <printReceipt v-model="printPageVisible" :serviceInvoiceData="serviceInvoice" />

    <!-- MAIN VIEW -->
    <div class="w-full h-screen px-8">
      <div class="center-all w-full flex-col">
        <div class="flex-start w-[1561px] mt-6">
          <p class="text-[25px] font-bold text-brand_gray3">
            Service Invoice List >
            <span class="text-[20px]"> View Service Invoice</span>
          </p>
        </div>

        <hr class="border-t-2 w-[1561px] border-brand_gray2 my-3" />
      </div>

      <form class="w-[1561px]">
        <div class="grid grid-cols-6 gap-4">
          <SectionDataEntry :selectedInvoice="serviceInvoice" />
          <SectionTotalSummary :selectedInvoice="serviceInvoice" />
        </div>

        <div class="flex justify-between items-center mt-6">
          <div class="flex gap-3">
            <button @click.prevent="openEditDialog()"
              class="min-w-[120px] py-3 rounded-lg bg-brand_orange2 text-white text-lg font-medium hover:bg-brand_orange1 transition">
              Edit
            </button>
          </div>

          <div class="flex gap-3">
            <RouterLink to="/serviceInvoice/list"
              class="min-w-[120px] py-3 rounded-lg border border-brand_orange2 text-brand_orange2 text-lg font-medium hover:bg-brand_orange1 hover:text-white transition text-center">
              Back
            </RouterLink>

            <button :disabled="enableEdit" @click.prevent="printPageVisible = true"
              class="min-w-[120px] py-3 rounded-lg bg-brand_orange2 text-white text-lg font-medium hover:bg-brand_orange1 transition disabled:opacity-50 disabled:cursor-not-allowed">
              Print
            </button>
          </div>
        </div>
      </form>
    </div>
  </main>
</template>

<script>

import SectionDataEntry from "../components/partial/ServiceInvoice/SectionDataEntry.vue";
import SectionTotalSummary from "../components/partial/ServiceInvoice/SectionTotalSummary.vue";
import printReceipt from "@/components/modals/printReceipt.vue";
import loading from "@/components/modals/loading.vue";
import { reactive, ref, onMounted, inject } from "vue";
import { useRouter } from "vue-router";

export default {
  components: {
    loading,
    printReceipt,
    SectionDataEntry,
    SectionTotalSummary,
  },
  props: {
    id: { type: String, required: true },
  },
  setup(props) {
    const router = useRouter();
    const axios = inject("$axios");
    const isLoading = ref(false);
    const serviceInvoice = ref({});
    const printPageVisible = ref(false);
    const enableEdit = ref(false);

    // Use reactive instead of ref for nested object
    const editDataPayload = reactive({});

    const selectedServiceInvoice = async () => {
      isLoading.value = true;
      try {
        const res = await axios.get(`/api/invoice/${props.id}`);
        serviceInvoice.value = res.data.data;
      } catch (e) {
        console.error(e);
        router.push("/serviceInvoice/list");
      } finally {
        isLoading.value = false;
      }
    };

    const openEditDialog = () => {
      const invoice = serviceInvoice.value;

      Object.assign(editDataPayload, {
        id: invoice.id,
        payeeId: invoice.payee.id,
        invoice_number: invoice.invoice_number || "",
        invoice_date: invoice.invoice_date ? invoice.invoice_date.split("T")[0] : "",
        soldTo: invoice.payee?.sold_to || "",
        terms: invoice.payee?.terms || "",
        tin: invoice.payee?.tin || "",
        oscaId: invoice.payee?.osca_pwd_id_no || "",
        address: invoice.payee?.address || "",
        signature: invoice.payee?.sc_pwd_signature || "",

        mode_of_payment: invoice.mode_of_payment || "cash",
        Check_no: invoice.Check_no || 0,
        Bank: invoice.Bank || "",
        VATable_sales: invoice.VATable_sales || 0,
        vat_Exempt_sales: invoice.vat_Exempt_sales || 0,
        zero_rated_sales: invoice.zero_rated_sales || 0,
        vat_amount: invoice.vat_amount || 0,
        amount: invoice.amount || 0,
        total_sales_vat_inclusive: invoice.total_sales_vat_inclusive || 0,
        less_vat: invoice.less_vat || 0,
        amount_net_of_vat: invoice.amount_net_of_vat || 0,
        less_sc_pwd_discount: invoice.less_sc_pwd_discount || 0,
        less_withholding_tax: invoice.less_withholding_tax || 0,
        total_amount_due: invoice.total_amount_due || 0,
        withWithholding: false,

        invoice_items:
          invoice.invoiceItems?.map((item) => ({
            id: item.id,
            description: item.description || "",
            quantity: item.quantity || 0,
            cost: item.cost || 0,
            total: item.total || 0,
          })) || [],
      });

      enableEdit.value = true;
      computeTransaction(); // compute immediately when opening dialog
    };

    const submitForm = async () => {
      isLoading.value = true;
      try {
        const res = await axios.put("/api/invoice/update", editDataPayload);
        console.log("Saved:", res.data);
        enableEdit.value = false;
      } catch (error) {
        console.error(error);
      } finally {
        isLoading.value = false;
      }
    };

    const computedResults = ref({});

    const computeTransaction = async () => {
      try {
        const res = await axios.get("/api/invoice/get/transaction/computation", {
          params: {
            withWithholding: editDataPayload.withWithholding || false,
            vatableSalesRaw: editDataPayload.VATable_sales || 0,
            zeroRatedSales: editDataPayload.zero_rated_sales || 0,
            vatExemptSales: editDataPayload.vat_Exempt_sales || 0,
          },
        });

        computedResults.value = res.data.data || {};

        const result = computedResults.value;

        console.log(result);

        editDataPayload.vat_amount = result.vatAmount || 0;
        editDataPayload.amount = result.amount || 0;
        editDataPayload.total_sales_vat_inclusive = result.amount || 0;
        editDataPayload.less_withholding_tax = result.lessWithholding || 0;
        editDataPayload.total_amount_due = result.totalAmountDue || 0;
      } catch (error) {
        console.error("Error computing transaction:", error);
      }
    };

    onMounted(selectedServiceInvoice);

    return {
      serviceInvoice,
      isLoading,
      printPageVisible,
      enableEdit,
      editDataPayload,
      openEditDialog,
      submitForm,
      computeTransaction,
      computedResults,
    };
  },
};
</script>
